import type { IBimRelationshipEdge } from '@skystone/shared';
import type { ICardLayout } from './bim-graph-layout';

/**
 * Orthogonal (90-degree) edge router for the BIM relationship graph.
 *
 * Each edge leaves a port on the right or left side of its source card and
 * enters a port on the facing side of its target card. The connecting line is
 * drawn as a horizontal -> vertical -> horizontal poly-line through a routing
 * channel placed between the two cards, so lines never run diagonally across
 * unrelated boxes. Ports are distributed along a card's edge and channels are
 * offset per edge so parallel lines do not overlap.
 */

export interface IPoint {
  x: number;
  y: number;
}

export interface IRoutedEdge {
  id: string;
  path: string;
  from: IPoint;
  to: IPoint;
  labelX: number;
  labelY: number;
}

type Side = 'left' | 'right';

interface IPortAssignment {
  side: Side;
  slot: number;
  count: number;
}

const CHANNEL_STEP = 16;
const SAME_LANE_BUS_GAP = 36;

function cardCenterY(card: ICardLayout): number {
  return card.y + card.height / 2;
}

function resolveSides(from: ICardLayout, to: ICardLayout): { fromSide: Side; toSide: Side } {
  if (from.x < to.x) {
    return { fromSide: 'right', toSide: 'left' };
  }

  if (from.x > to.x) {
    return { fromSide: 'left', toSide: 'right' };
  }

  return { fromSide: 'right', toSide: 'right' };
}

function portKey(cardId: string, side: Side): string {
  return `${cardId}|${side}`;
}

function buildPortAssignments(
  edges: IBimRelationshipEdge[],
  cards: Map<string, ICardLayout>,
): Map<string, { from: IPortAssignment; to: IPortAssignment }> {
  const buckets = new Map<string, { endpointId: string; otherY: number }[]>();
  const edgeSides = new Map<string, { fromSide: Side; toSide: Side }>();

  const pushEndpoint = (key: string, endpointId: string, otherY: number): void => {
    const bucket = buckets.get(key) ?? [];
    bucket.push({ endpointId, otherY });
    buckets.set(key, bucket);
  };

  for (const edge of edges) {
    const from = cards.get(edge.fromTable);
    const to = cards.get(edge.toTable);
    if (!from || !to) {
      continue;
    }

    const sides = resolveSides(from, to);
    edgeSides.set(edge.id, sides);
    pushEndpoint(portKey(from.id, sides.fromSide), `${edge.id}|from`, cardCenterY(to));
    pushEndpoint(portKey(to.id, sides.toSide), `${edge.id}|to`, cardCenterY(from));
  }

  const assignments = new Map<string, { from: IPortAssignment; to: IPortAssignment }>();
  for (const [edgeId, sides] of edgeSides) {
    assignments.set(edgeId, {
      from: { side: sides.fromSide, slot: 0, count: 1 },
      to: { side: sides.toSide, slot: 0, count: 1 },
    });
  }

  for (const entries of buckets.values()) {
    entries.sort((a, b) => a.otherY - b.otherY || a.endpointId.localeCompare(b.endpointId));
    entries.forEach((entry, slot) => {
      // The endpoint id is `${edge.id}|from|to-suffix`; the side suffix is
      // after the LAST separator so edge ids that contain '|' still resolve.
      const separator = entry.endpointId.lastIndexOf('|');
      const assignment = assignments.get(entry.endpointId.slice(0, separator));
      if (!assignment) {
        return;
      }

      const port = entry.endpointId.slice(separator + 1) === 'from' ? assignment.from : assignment.to;
      port.slot = slot;
      port.count = entries.length;
    });
  }

  return assignments;
}

function portPoint(card: ICardLayout, assignment: IPortAssignment): IPoint {
  const x = assignment.side === 'right' ? card.x + card.width : card.x;
  const y = card.y + ((assignment.slot + 1) / (assignment.count + 1)) * card.height;
  return { x, y };
}

function channelOffsets(edges: IBimRelationshipEdge[], cards: Map<string, ICardLayout>): Map<string, number> {
  const grouped = new Map<string, string[]>();
  for (const edge of edges) {
    const from = cards.get(edge.fromTable);
    const to = cards.get(edge.toTable);
    if (!from || !to) {
      continue;
    }

    const key = from.lane <= to.lane ? `${from.lane}|${to.lane}` : `${to.lane}|${from.lane}`;
    const bucket = grouped.get(key) ?? [];
    bucket.push(edge.id);
    grouped.set(key, bucket);
  }

  const offsets = new Map<string, number>();
  for (const ids of grouped.values()) {
    const middle = (ids.length - 1) / 2;
    ids.forEach((id, index) => offsets.set(id, (index - middle) * CHANNEL_STEP));
  }
  return offsets;
}

function buildPath(start: IPoint, end: IPoint, midX: number): string {
  return `M ${start.x} ${start.y} L ${midX} ${start.y} L ${midX} ${end.y} L ${end.x} ${end.y}`;
}

export function routeEdges(
  edges: IBimRelationshipEdge[],
  cards: Map<string, ICardLayout>,
): Map<string, IRoutedEdge> {
  const assignments = buildPortAssignments(edges, cards);
  const offsets = channelOffsets(edges, cards);
  const routed = new Map<string, IRoutedEdge>();

  for (const edge of edges) {
    const fromCard = cards.get(edge.fromTable);
    const toCard = cards.get(edge.toTable);
    const assignment = assignments.get(edge.id);
    if (!fromCard || !toCard || !assignment) {
      continue;
    }

    const start = portPoint(fromCard, assignment.from);
    const end = portPoint(toCard, assignment.to);
    const offset = offsets.get(edge.id) ?? 0;

    const sameLane = assignment.from.side === assignment.to.side;
    const rightEdge = Math.max(start.x, end.x);
    // For same-lane edges the bus must stay outside the card even when a large
    // channel offset is negative, otherwise the vertical segment re-enters the
    // card body. Clamp it to just past the card's right edge.
    const midX = sameLane
      ? Math.max(rightEdge + SAME_LANE_BUS_GAP + offset, rightEdge + 1)
      : (start.x + end.x) / 2 + offset;

    routed.set(edge.id, {
      id: edge.id,
      path: buildPath(start, end, midX),
      from: start,
      to: end,
      labelX: midX,
      labelY: (start.y + end.y) / 2,
    });
  }

  return routed;
}
